import { z } from 'zod';
import { areaOfService } from './areas.js';
/**
 * Serviços prontos: o especialista recebe um pedido, usa os dados que o dono
 * já tem com ele e devolve um entregável (documento, lista, plano com datas,
 * carta). Tudo fica guardado em "Entregas". O especialista organiza, prepara e
 * redige; quem decide, confere e envia é o dono.
 */
export const SERVICE_KEYS = [
    'raio_x',
    'assinaturas',
    'carta',
    'viagem',
    'festa',
    'dossie',
    'semana',
    'irpf',
    'mei',
    'carreira',
    'estudos',
    'segunda',
    'fecho',
];
export const serviceKeySchema = z.enum(SERVICE_KEYS);
const RAW_SERVICE_CATALOG = [
    {
        key: 'raio_x',
        title: 'Raio-X do mês',
        promise: 'Para onde vai o dinheiro, o que vence e onde dá pra aliviar.',
        specialist: 'contas',
        mascot: { key: 'esp-contas', name: 'Dinheiro' },
        includes: [
            'Quanto já saiu e quanto ainda vence no mês',
            'As contas que mais pesam',
            'O que está atrasado ou vence nos próximos dias',
            'Três pontos de atenção para o mês',
        ],
        scope: 'Organização das suas contas. Não é recomendação de investimento.',
    },
    {
        key: 'assinaturas',
        title: 'Caça-assinaturas',
        promise: 'Tudo o que se repete todo mês, quanto soma no ano e o que revisar.',
        specialist: 'contas',
        mascot: { key: 'esp-contas', name: 'Dinheiro' },
        includes: [
            'Cada cobrança que se repete, por tipo',
            'Quanto cada uma custa no ano',
            'Serviços parecidos que se sobrepõem',
            'O que vale revisar ou cancelar',
        ],
        scope: null,
    },
    {
        key: 'carta',
        title: 'Reclamações e cartas',
        promise: 'A carta pronta para reclamar, cancelar ou pedir reembolso.',
        specialist: 'cartas',
        mascot: { key: 'esp-cartas', name: 'Selo' },
        includes: [
            'Texto claro, firme e educado, com o seu pedido',
            'Os direitos que costumam valer nesse tipo de caso',
            'O que anexar e onde enviar',
            'Os campos que você completa antes de mandar',
        ],
        scope: 'Modelo de carta para você conferir, completar e enviar em seu nome. Não é orientação jurídica sobre o seu caso e não diz se você vai ter razão. Para ajuda gratuita, procure o Procon, o consumidor.gov.br ou a Defensoria Pública.',
    },
    {
        key: 'viagem',
        title: 'Roteiro de viagem',
        promise: 'O dia a dia da viagem, os documentos e os lembretes na agenda.',
        specialist: 'passagens',
        mascot: { key: 'esp-passagens', name: 'Rotina' },
        includes: [
            'Roteiro dia a dia, no ritmo que você escolher',
            'Documentos e o que levar',
            'Lembretes de ida, volta e preparação na agenda',
        ],
        scope: 'Confira horários, preços e regras de entrada nos sites oficiais antes de viajar.',
    },
    {
        key: 'festa',
        title: 'Festa organizada',
        promise: 'Checklist com datas, lista de compras e convite pronto.',
        specialist: 'datas_importantes',
        mascot: { key: 'esp-datas', name: 'Rotina' },
        includes: [
            'O que fazer em cada semana até o dia',
            'Lista de compras para a quantidade de convidados',
            'Convite pronto para mandar',
            'Lembretes na agenda e itens na sua lista de compras',
        ],
        scope: null,
    },
    {
        key: 'dossie',
        title: 'Dossiê de um assunto',
        promise: 'O que se sabe agora sobre um assunto, de várias fontes, com os links.',
        specialist: 'noticias',
        mascot: { key: 'esp-noticias', name: 'Informação' },
        includes: [
            'O que se sabe, em poucas linhas',
            'Os pontos principais e o que observar',
            'As fontes, com o link de cada uma',
        ],
        scope: 'Resumo com palavras nossas a partir de notícias públicas. Leia a íntegra no veículo.',
    },
    {
        key: 'semana',
        title: 'Resumo da semana',
        promise: 'O que marcou a semana nos seus assuntos, em uma leitura.',
        specialist: 'noticias',
        mascot: { key: 'esp-noticias', name: 'Informação' },
        includes: [
            'Os destaques de cada assunto que você segue',
            'Por que importa, em uma linha',
            'As fontes, com o link de cada uma',
        ],
        scope: 'Resumo com palavras nossas a partir de notícias públicas. Leia a íntegra no veículo.',
    },
    {
        key: 'irpf',
        title: 'Prévia do Imposto de Renda',
        promise: 'Organiza seus números, soma as deduções e mostra qual modelo tende a compensar.',
        specialist: 'contas',
        mascot: { key: 'esp-contas', name: 'Dinheiro' },
        includes: [
            'Suas rendas e deduções somadas, com os limites de cada uma',
            'Simplificado ou completo: qual tende a compensar',
            'Os documentos que você vai precisar',
        ],
        scope: 'Prévia para você conferir. Não é a declaração e não substitui a Receita nem um contador.',
    },
    {
        key: 'mei',
        title: 'MEI em dia',
        promise: 'Quanto do limite você já usou, o que vence e os lembretes do mês.',
        specialist: 'contas',
        mascot: { key: 'esp-contas', name: 'Dinheiro' },
        includes: [
            'Quanto do limite anual já foi usado e a projeção até dezembro',
            'O que fazer todo mês e na declaração anual',
            'Lembretes do DAS na agenda',
        ],
        scope: 'Organização das obrigações do MEI. Para dúvidas do seu caso, fale com um contador.',
    },
    {
        key: 'carreira',
        title: 'Currículo e entrevista',
        promise: 'Seu currículo pronto para a vaga e um treino de entrevista.',
        specialist: 'carreira',
        mascot: { key: 'esp-carreira', name: 'Trabalho' },
        includes: [
            'Currículo de uma página, direto ao ponto, para a vaga que você quer',
            'As perguntas mais prováveis da entrevista',
            'Como responder cada uma, com o que você já fez',
        ],
        scope: 'Use só o que é verdade sobre você. Direito trabalhista, só como informação geral.',
    },
    {
        key: 'estudos',
        title: 'Plano de estudos',
        promise: 'O que estudar em cada semana até a sua data, com lembretes.',
        specialist: 'estudos',
        mascot: { key: 'esp-estudos', name: 'Lume' },
        includes: [
            'Semana a semana até a data que você escolher',
            'Revisões espaçadas e simulados no ritmo certo',
            'Lembretes na agenda',
        ],
        scope: null,
    },
    {
        key: 'segunda',
        title: 'Reunião de segunda',
        promise: 'A sua semana em uma página: compromissos, contas, datas e prioridades.',
        specialist: 'bom_dia',
        mascot: { key: 'esp-bomdia-v2', name: 'Rotina' },
        includes: [
            'Compromissos e lembretes dos próximos 7 dias',
            'Contas que vencem e datas importantes da semana',
            'Três prioridades para a semana',
        ],
        scope: null,
    },
    {
        key: 'fecho',
        title: 'Fecho do mês',
        promise: 'O que o mês teve: contas pagas, compromissos, entregas e o que vem.',
        specialist: 'retrospectiva',
        mascot: { key: 'esp-retro', name: 'Dinheiro' },
        includes: [
            'Contas pagas e o que ficou para o mês que vem',
            'Compromissos, reuniões e entregas do mês',
            'Três destaques e um ponto de atenção',
        ],
        scope: null,
    },
];
/** Quem assina cada serviço é o personagem da área dele. */
export const SERVICE_CATALOG = RAW_SERVICE_CATALOG.map((service) => {
    const area = areaOfService(service.key);
    return area
        ? { ...service, mascot: { key: area.character.key, name: area.character.name } }
        : service;
});
export function findService(key) {
    return SERVICE_CATALOG.find((service) => service.key === key);
}
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a data no formato AAAA-MM-DD.');
export const CARTA_TYPES = [
    'reclamacao',
    'cancelamento',
    'reembolso',
    'arrependimento',
    'voo',
];
export const cartaTypeSchema = z.enum(CARTA_TYPES);
export const CARTA_TYPE_LABEL = {
    reclamacao: 'Reclamar de um produto ou serviço',
    cancelamento: 'Cancelar um serviço ou assinatura',
    reembolso: 'Pedir o dinheiro de volta',
    arrependimento: 'Desistir de uma compra feita pela internet',
    voo: 'Voo atrasado, cancelado ou bagagem',
};
/** O que cada serviço precisa para começar. */
export const serviceRequestSchema = z.discriminatedUnion('service', [
    z.object({ service: z.literal('raio_x') }),
    z.object({
        service: z.literal('assinaturas'),
        /** Cobranças coladas da fatura ou do extrato, além das contas que eu já acompanho. */
        extra: z.string().trim().max(4000).optional(),
    }),
    z.object({
        service: z.literal('carta'),
        type: cartaTypeSchema,
        company: z.string().trim().min(2).max(120),
        story: z.string().trim().min(10).max(2000),
        wish: z.string().trim().max(400).optional(),
    }),
    z.object({
        service: z.literal('viagem'),
        destination: z.string().trim().min(2).max(120),
        start: isoDate,
        end: isoDate,
        people: z.number().int().min(1).max(20),
        style: z.string().trim().max(300).optional(),
    }),
    z.object({ service: z.literal('dossie'), topic: z.string().trim().min(3).max(80) }),
    z.object({ service: z.literal('semana') }),
    z.object({
        service: z.literal('irpf'),
        /** Valores do ano, em centavos. */
        salaryCents: z.number().int().min(0).max(10_000_000_000),
        otherIncomeCents: z.number().int().min(0).max(10_000_000_000).default(0),
        inssCents: z.number().int().min(0).max(1_000_000_000).default(0),
        withheldCents: z.number().int().min(0).max(1_000_000_000).default(0),
        dependents: z.number().int().min(0).max(20).default(0),
        medicalCents: z.number().int().min(0).max(1_000_000_000).default(0),
        educationCents: z.number().int().min(0).max(1_000_000_000).default(0),
        pgblCents: z.number().int().min(0).max(1_000_000_000).default(0),
    }),
    z.object({
        service: z.literal('mei'),
        activity: z.enum(['comercio', 'servicos', 'ambos']),
        revenueCents: z.number().int().min(0).max(10_000_000_000),
        /** Mês em que o MEI abriu, quando foi neste ano (limite proporcional). */
        openedMonth: z.number().int().min(1).max(12).nullable().default(null),
    }),
    z.object({
        service: z.literal('carreira'),
        role: z.string().trim().min(2).max(120),
        experience: z.string().trim().min(10).max(3000),
        education: z.string().trim().max(800).optional(),
        skills: z.string().trim().max(800).optional(),
    }),
    z.object({
        service: z.literal('estudos'),
        goal: z.string().trim().min(3).max(200),
        deadline: isoDate,
        hoursPerWeek: z.number().int().min(1).max(60),
        level: z.string().trim().max(200).optional(),
    }),
    z.object({ service: z.literal('segunda') }),
    z.object({ service: z.literal('fecho') }),
    z.object({
        service: z.literal('festa'),
        occasion: z.string().trim().min(2).max(120),
        date: isoDate,
        guests: z.number().int().min(1).max(500),
        budgetCents: z.number().int().min(0).max(100_000_000).optional(),
        place: z.string().trim().max(160).optional(),
    }),
]);
// ── O documento entregue ─────────────────────────────────────────────────
export const docBlockSchema = z.discriminatedUnion('type', [
    /** Parágrafos de texto corrido. */
    z.object({ type: z.literal('text'), paragraphs: z.array(z.string()) }),
    /** Números de destaque (até 4), lado a lado. */
    z.object({
        type: z.literal('facts'),
        items: z.array(z.object({
            label: z.string(),
            value: z.string(),
            tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
        })),
    }),
    /** Lista com valor à direita (contas, cobranças, itens). */
    z.object({
        type: z.literal('list'),
        items: z.array(z.object({
            label: z.string(),
            value: z.string().nullable().default(null),
            note: z.string().nullable().default(null),
            /** Link da fonte, quando o item é uma notícia. */
            url: z.url().nullable().optional(),
        })),
    }),
    /** Tarefas com quando fazer. */
    z.object({
        type: z.literal('checklist'),
        items: z.array(z.object({ when: z.string().nullable().default(null), text: z.string() })),
    }),
    /** Linha do tempo: dia a dia, semana a semana, vencimentos. */
    z.object({
        type: z.literal('timeline'),
        items: z.array(z.object({
            when: z.string(),
            title: z.string(),
            detail: z.string().nullable().default(null),
            tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
        })),
    }),
    /** Barras horizontais (valores sempre calculados por código). */
    z.object({
        type: z.literal('bars'),
        items: z.array(z.object({
            label: z.string(),
            value: z.number().nonnegative(),
            display: z.string(),
            tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
        })),
    }),
    /** Medidor: quanto de um limite já foi usado (1 = o limite inteiro). */
    z.object({
        type: z.literal('meter'),
        label: z.string(),
        value: z.number().nonnegative(),
        display: z.string(),
        caption: z.string().nullable().default(null),
        marks: z.array(z.object({ at: z.number().nonnegative(), label: z.string() })).default([]),
        tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
    }),
    /** Comparação lado a lado (2 ou 3 opções), com a melhor marcada. */
    z.object({
        type: z.literal('compare'),
        options: z
            .array(z.object({
            title: z.string(),
            value: z.string(),
            lines: z.array(z.string()).default([]),
            best: z.boolean().default(false),
        }))
            .min(2)
            .max(3),
    }),
    /** Cartões que deslizam para o lado (pontos de um dossiê, ideias). */
    z.object({
        type: z.literal('cards'),
        items: z.array(z.object({
            kicker: z.string().nullable().default(null),
            title: z.string(),
            text: z.string(),
        })),
    }),
    /** Carta pronta. Trechos entre colchetes são para a pessoa completar. */
    z.object({
        type: z.literal('letter'),
        to: z.string(),
        subject: z.string(),
        paragraphs: z.array(z.string()),
        closing: z.string(),
    }),
]);
export const deliveryDocumentSchema = z.object({
    title: z.string(),
    subtitle: z.string().nullable().default(null),
    /** Capa: o número que resume a entrega, em destaque. */
    cover: z
        .object({
        value: z.string(),
        label: z.string(),
        tone: z.enum(['neutral', 'good', 'warn']).default('neutral'),
    })
        .nullable()
        .default(null),
    sections: z.array(z.object({ heading: z.string().nullable().default(null), blocks: z.array(docBlockSchema) })),
    /** Aviso de escopo ou de conferência, no rodapé. */
    note: z.string().nullable().default(null),
});
/** O que o dono pode fazer com um toque a partir da entrega. */
export const deliveryActionSchema = z.discriminatedUnion('kind', [
    z.object({
        id: z.string(),
        kind: z.literal('agenda'),
        label: z.string(),
        items: z.array(z.object({ title: z.string(), date: isoDate, time: z.string().nullable() })),
        done: z.boolean().default(false),
    }),
    z.object({
        id: z.string(),
        kind: z.literal('compras'),
        label: z.string(),
        items: z.array(z.object({ name: z.string(), quantity: z.number().positive() })),
        done: z.boolean().default(false),
    }),
]);
export const deliverySummarySchema = z.object({
    id: z.uuid(),
    service: serviceKeySchema,
    title: z.string(),
    subtitle: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const deliverySchema = deliverySummarySchema.extend({
    document: deliveryDocumentSchema,
    actions: z.array(deliveryActionSchema),
    /** Texto pronto para copiar ou mandar (carta, convite), quando houver. */
    shareText: z.string().nullable(),
});
export const serviceQuotaSchema = z.object({
    /** Entregas por mês no plano atual (0 = o plano não inclui); no Essencial, a amostra única. */
    limit: z.number().int().min(0),
    used: z.number().int().min(0),
    remaining: z.number().int().min(0),
    /** true = amostra única (não renova no mês). */
    sample: z.boolean().default(false),
});
export const deliveryListSchema = z.object({
    deliveries: z.array(deliverySummarySchema),
    quota: serviceQuotaSchema,
});
